// src/lib/countryTranslations.ts
import countries from "i18n-iso-countries";
import arLocale from "i18n-iso-countries/langs/ar.json";
import csLocale from "i18n-iso-countries/langs/cs.json";
import daLocale from "i18n-iso-countries/langs/da.json";
import deLocale from "i18n-iso-countries/langs/de.json";
import elLocale from "i18n-iso-countries/langs/el.json";
import enLocale from "i18n-iso-countries/langs/en.json";
import esLocale from "i18n-iso-countries/langs/es.json";
import fiLocale from "i18n-iso-countries/langs/fi.json";
import frLocale from "i18n-iso-countries/langs/fr.json";
import itLocale from "i18n-iso-countries/langs/it.json";
import jaLocale from "i18n-iso-countries/langs/ja.json";
import koLocale from "i18n-iso-countries/langs/ko.json";
import nlLocale from "i18n-iso-countries/langs/nl.json";
import noLocale from "i18n-iso-countries/langs/no.json";
import ptLocale from "i18n-iso-countries/langs/pt.json";
import ruLocale from "i18n-iso-countries/langs/ru.json";
import svLocale from "i18n-iso-countries/langs/sv.json";
import trLocale from "i18n-iso-countries/langs/tr.json";
import ukLocale from "i18n-iso-countries/langs/uk.json";
import zhLocale from "i18n-iso-countries/langs/zh.json";

import type { Language } from "@/translations/supportedLanguages";

const localeMap: Record<Language, countries.LocaleData> = {
  en: enLocale,
  ar: arLocale,
  cs: csLocale,
  da: daLocale,
  de: deLocale,
  el: elLocale,
  es: esLocale,
  fi: fiLocale,
  fr: frLocale,
  it: itLocale,
  ja: jaLocale,
  ko: koLocale,
  nl: nlLocale,
  no: noLocale,
  pt: ptLocale,
  ru: ruLocale,
  sv: svLocale,
  tr: trLocale,
  uk: ukLocale,
  zh: zhLocale,
};

const loadedLocales = new Set<Language>();

export function registerCountryLocale(lang: Language): void {
  const localeData = localeMap[lang] || localeMap.en;
  if (!loadedLocales.has(lang)) {
    countries.registerLocale(localeData as any);
    loadedLocales.add(lang);
  }
}

const customTranslations: Partial<
  Record<string, Partial<Record<Language, string>>>
> = {
  AN: {
    en: "Netherlands Antilles",
    ar: "جزر الأنتيل الهولندية",
    es: "Antillas Neerlandesas",
    fr: "Antilles néerlandaises",
    de: "Niederländische Antillen",
    pt: "Antilhas Holandesas",
    zh: "荷属安的列斯",
    ru: "Нидерландские Антильские острова",
    tr: "Hollanda Antilleri",
    ja: "オランダ領アンティル",
    ko: "네덜란드령 안틸레스",
    it: "Antille Olandesi",
    nl: "Nederlandse Antillen",
  },
  IC: {
    en: "Canary Islands",
    ar: "جزر الكناري",
    es: "Islas Canarias",
    fr: "Îles Canaries",
    de: "Kanarische Inseln",
    pt: "Ilhas Canárias",
    zh: "加那利群岛",
    ru: "Канарские острова",
    tr: "Kanarya Adaları",
    ja: "カナリア諸島",
    ko: "카나리 제도",
    it: "Isole Canarie",
    nl: "Canarische Eilanden",
  },
  CYP: {
    en: "Northern Cyprus",
    ar: "قبرص الشمالية",
    es: "Chipre del Norte",
    fr: "Chypre du Nord",
    de: "Nordzypern",
    pt: "Chipre do Norte",
    zh: "北塞浦路斯",
    ru: "Северный Кипр",
    tr: "Kuzey Kıbrıs",
    ja: "北キプロス",
    ko: "북키프로스",
    it: "Cipro del Nord",
    nl: "Noord-Cyprus",
  },
};

export function getCountryName(iso: string, lang: Language): string {
  const upper = iso.toUpperCase();
  const custom = customTranslations[upper]?.[lang];
  if (custom) return custom;
  const translated = countries.getName(upper, lang);
  if (translated) return translated;
  const fallbackCustomEn = customTranslations[upper]?.en;
  if (fallbackCustomEn) return fallbackCustomEn;
  const enName = countries.getName(upper, "en");
  if (enName) return enName;
  return upper;
}
